import { useEffect, useState } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";
import type { PageContent } from "@/types/content";

const PAGE_LABELS: Record<string, string> = {
  home: "Inicio",
  bodegas: "Bodegas",
  planes: "Planes",
  ubicacion: "Ubicación",
  seguridad: "Seguridad",
  nosotros: "Nosotros",
  "preguntas-frecuentes": "Preguntas frecuentes",
  contacto: "Contacto",
  terminos: "Términos y condiciones",
  privacidad: "Política de privacidad",
};

const PAGE_ORDER = Object.keys(PAGE_LABELS);

export default function AdminPages() {
  const [slug, setSlug] = useState("home");
  const [values, setValues] = useState<Partial<PageContent>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  async function load(s: string) {
    setLoading(true);
    setMessage(null);
    const res = await fetch(`${API_BASE}/bootstrap`);
    const json = await res.json();
    setValues(json.pages[s] ?? {});
    setLoading(false);
  }

  useEffect(() => {
    load(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/pages/${slug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        setMessage({ type: "error", text: "No se pudo guardar" });
        return;
      }
      setMessage({ type: "success", text: "Cambios guardados." });
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm";
  const labelCls = "block text-sm font-medium text-foreground-700 mb-1.5";
  const hasBody = slug === "nosotros" || slug === "terminos" || slug === "privacidad";
  const hasCta = !hasBody || slug === "nosotros";

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Páginas</h1>
      <p className="mt-1 text-sm text-foreground-500">Título, descripción e imagen del encabezado de cada página.</p>

      <select className={`mt-4 max-w-xs ${inputCls}`} value={slug} onChange={(e) => setSlug(e.target.value)}>
        {PAGE_ORDER.map((s) => (
          <option key={s} value={s}>
            {PAGE_LABELS[s]}
          </option>
        ))}
      </select>

      {message && (
        <div
          className={`mt-4 rounded-md p-3 text-sm border ${
            message.type === "success" ? "bg-primary-100 border-primary-200 text-primary-800" : "bg-accent-100 border-accent-200 text-accent-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <p className="mt-6 text-sm text-foreground-500">Cargando...</p>
      ) : (
        <div className="mt-6 space-y-4">
          <div>
            <label className={labelCls}>Título</label>
            <input
              className={inputCls}
              value={values.hero_titulo ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, hero_titulo: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelCls}>Descripción</label>
            <textarea
              className={inputCls}
              rows={3}
              value={values.hero_descripcion ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, hero_descripcion: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelCls}>URL de la imagen de encabezado</label>
            <input
              className={inputCls}
              value={values.hero_imagen_url ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, hero_imagen_url: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelCls}>Texto alternativo de la imagen</label>
            <input
              className={inputCls}
              value={values.hero_imagen_alt ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, hero_imagen_alt: e.target.value }))}
            />
          </div>

          {hasBody && (
            <div>
              <label className={labelCls}>
                Texto de la página (usa líneas que empiecen con "## " para los subtítulos, y deja una línea en
                blanco entre secciones)
              </label>
              <textarea
                className={`${inputCls} font-mono`}
                rows={12}
                value={values.body_rich_text ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, body_rich_text: e.target.value }))}
              />
            </div>
          )}

          {hasCta && (
            <>
              <div>
                <label className={labelCls}>
                  {slug === "nosotros" ? 'Título del bloque "Hablemos de tu proyecto"' : "Título del CTA final (dejar vacío para usar el texto por defecto)"}
                </label>
                <input
                  className={inputCls}
                  value={values.cta_titulo ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, cta_titulo: e.target.value }))}
                />
              </div>
              <div>
                <label className={labelCls}>
                  {slug === "nosotros" ? "Descripción de ese bloque" : "Descripción del CTA final"}
                </label>
                <textarea
                  className={inputCls}
                  rows={2}
                  value={values.cta_descripcion ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, cta_descripcion: e.target.value }))}
                />
              </div>
            </>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
          >
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
